// ─────────────────────────────────────────────────────────────────
// Glass-i — Stage 6JR.1 Proforma print/preview helpers (PURE).
//
// The Proforma print reuses the existing report engine exactly like the
// subscription invoice: create a report job, then open the shared viewer
// route /reports/viewer/:jobId (poll + HTML iframe + browser Print/Save-PDF).
// Keeping the request shape in a pure module makes it unit-testable and keeps
// the button component a thin shell. NO new endpoint, NO save-workflow change.
// ─────────────────────────────────────────────────────────────────

export const PROFORMA_REPORT_CODE = 'erp_proforma';

// Area units the print may render the MAIN line table in (per-print choice).
// Default is 'SQM' (historical behaviour); 'SQFT' shows the area column + rate
// in square feet. Anything else is normalised to 'SQM'.
export const PROFORMA_AREA_UNITS = ['SQM', 'SQFT'];
export function normalizeAreaUnit(u) {
  return String(u || '').toUpperCase() === 'SQFT' ? 'SQFT' : 'SQM';
}

// Build the createJob payload for a saved proforma's print. `inputParams`
// mirrors the report_jobs.input_params column (same wire field the invoice
// viewer uses). Throws when no id so the caller never queues an empty job.
// `areaUnit` picks the main line-table unit (SQM default / SQFT); it flows
// through report_jobs.input_params to the dataset — no other workflow changes.
export function proformaReportJobRequest(proformaId, areaUnit = 'SQM') {
  const id = proformaId == null ? '' : String(proformaId).trim();
  if (!id) throw new Error('proformaId is required to print a proforma');
  return {
    reportCode: PROFORMA_REPORT_CODE,
    // Real paginated PDF — exact, printer-safe page breaks (browser HTML
    // print re-paginates the flat HTML and cuts rows). The engine renders
    // PDF via the report-service; the viewer shows it and prints it directly.
    format: 'pdf',
    inputParams: { proformaId: id, areaUnit: normalizeAreaUnit(areaUnit) }
  };
}

// The shared single-viewer route the invoice flow also uses.
export function proformaReportViewerPath(jobId) {
  return `/reports/viewer/${encodeURIComponent(String(jobId))}`;
}

// A proforma can be printed once it has been saved (has an id). Every lifecycle
// status (PENDING / CONFIRMED / HOLD / CANCELLED) has a persisted document.
export function canPrintProforma(record) {
  return Boolean(record && record.id);
}
